import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, HeadphonesIcon, Volume2Icon } from 'lucide-react';
const AUDIO_SRC = 'https://cdn.magicpatterns.com/uploads/nPDTmvN6Qxk3iynqTRwm93/AUDIO_PREVIO.mp3';
const BAR_COUNT = 5;
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
export function AudioPreview() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataRef = useRef<Uint8Array | null>(null);
  const rafRef = useRef<number>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [levels, setLevels] = useState<number[]>(Array(BAR_COUNT).fill(0.25));
  // Set up Web Audio analyser lazily on first play (requires user gesture)
  const ensureAnalyser = () => {
    if (audioCtxRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audioRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch {
      // CORS-tainted or unsupported — we fall back to synthetic bars
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
  };
  const animate = () => {
    const analyser = analyserRef.current;
    const data = dataRef.current;
    let next: number[];
    if (analyser && data) {
      analyser.getByteFrequencyData(data);
      const total = data.reduce((a, b) => a + b, 0);
      if (total > 0) {
        // Sample a few bins across the spectrum for each bar
        next = Array.from({
          length: BAR_COUNT
        }, (_, i) => {
          const idx = Math.floor(i / BAR_COUNT * data.length * 0.7) + 1;
          return Math.min(1, Math.max(0.15, data[idx] / 200));
        });
      } else {
        next = synthetic();
      }
    } else {
      next = synthetic();
    }
    setLevels(next);
    rafRef.current = requestAnimationFrame(animate);
  };
  const synthetic = (): number[] => {
    const t = performance.now() / 220;
    return Array.from({
      length: BAR_COUNT
    }, (_, i) => {
      const wave = Math.sin(t + i * 0.9) * 0.5 + 0.5;
      const jitter = Math.random() * 0.25;
      return Math.min(1, Math.max(0.18, wave * 0.8 + jitter));
    });
  };
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      ensureAnalyser();
      audioCtxRef.current?.resume?.();
      audio.play();
    }
  };
  useEffect(() => {
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // settle to idle
      setLevels(Array(BAR_COUNT).fill(0.25));
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);
  const progress = duration > 0 ? current / duration * 100 : 0;
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = Math.min(duration, Math.max(0, ratio * duration));
  };
  return <div className="w-full max-w-xl mx-auto" data-id="element-55">
      <div className="text-peach text-xs font-semibold tracking-[0.3em] uppercase text-center mb-4" data-id="element-56">
        Audio de muestra
      </div>

      <div className="bg-[#0d2b24] border border-cream/10 rounded-3xl p-5 md:p-6 shadow-2xl" data-id="element-57">
        <div className="flex items-center gap-4 md:gap-5" data-id="element-58">
          {/* Play / Pause */}
          <button onClick={togglePlay} aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'} className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-peach text-forest flex items-center justify-center shadow-[0_0_25px_rgba(232,160,122,0.4)] hover:scale-105 active:scale-95 transition-transform" data-id="element-59">
            {isPlaying ? <PauseIcon className="w-7 h-7 fill-forest" data-id="element-60" /> : <PlayIcon className="w-7 h-7 fill-forest translate-x-0.5" data-id="element-61" />}
          </button>

          {/* Title + subtitle */}
          <div className="min-w-0 flex-1" data-id="element-62">
            <div className="flex items-center gap-2 text-cream" data-id="element-63">
              <HeadphonesIcon className="w-5 h-5 text-peach shrink-0" data-id="element-64" />
              <span className="font-bold text-lg md:text-xl truncate" data-id="element-65">
                Audio de muestra
              </span>
            </div>
            <p className="text-sand/70 text-sm md:text-base mt-0.5" data-id="element-66">
              Escúchalo antes de comprar
            </p>
          </div>

          {/* Animated bars */}
          <div className="shrink-0 flex items-end gap-1 h-9" aria-hidden="true" data-id="element-67">
            {levels.map((lvl, i) => <span key={i} className="w-1.5 rounded-full bg-peach transition-[height] duration-100 ease-out" style={{
            height: `${Math.round(lvl * 100)}%`,
            opacity: 0.85
          }} data-id="element-68" />)}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6 h-1.5 rounded-full bg-cream/10 cursor-pointer overflow-hidden" onClick={seek} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} data-id="element-69">
          <div className="h-full bg-peach rounded-full" style={{
          width: `${progress}%`
        }} data-id="element-70" />
        </div>

        {/* Footer row */}
        <div className="mt-3 flex items-center justify-between text-sand/70 text-sm" data-id="element-71">
          <span className="tabular-nums" data-id="element-72">{formatTime(current)}</span>
          <span className="flex items-center gap-2 tracking-[0.2em] uppercase text-xs" data-id="element-73">
            <Volume2Icon className="w-4 h-4 text-peach" data-id="element-74" />
            Muestra
          </span>
          <span className="tabular-nums" data-id="element-75">{formatTime(duration)}</span>
        </div>
      </div>

      <audio ref={audioRef} src={AUDIO_SRC} crossOrigin="anonymous" preload="metadata" onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => setIsPlaying(false)} onTimeUpdate={e => setCurrent(e.currentTarget.currentTime)} onLoadedMetadata={e => setDuration(e.currentTarget.duration)} data-id="element-76" />
    </div>;
}